import React from "react";
import { useDispatch } from "react-redux";

import {
  ExperienceListDto,
  HistoryDto,
  OrganizationDto,
} from "@intelligems/ig-types";
import { useGetHistory } from "@src/api/histories/get-history";
import { useBooleanWebUiScopeMap } from "@src/components/LimitAccess/check-scopes-hooks";
import {
  useCheckFreeTrialStatus,
  useCheckSubscriptionStatus,
} from "@src/components/LimitAccess/check-subscriptions-hooks";
import { useIsPriceTestIntegrationReady } from "@src/components/LimitAccess/experiment-scope-hooks";
import { ScopedStartExperienceWithDisabled } from "@src/components/LimitAccess/ScopedStartExperienceWithDisabled";
import { Experience } from "@src/store/slices/experiences/types";
import { setDialog } from "@src/store/slices/global";
import { useIsMutating } from "@tanstack/react-query";

import { ActionExperienceButton } from "./ActionExperienceButton";

interface StartExperienceButtonProps {
  rowExperience: Experience;
  currentExperience?: Experience;
  experiencesList: ExperienceListDto[];
  organization: OrganizationDto;
  renderLocation: "overview" | "experience";
  onStartButtonClick: (
    rowExperience: Experience,
    history: HistoryDto[],
    isSubscriptionStartActive?: boolean
  ) => void;
  hasSchedule: boolean;
}

export const StartExperienceButton = ({
  experiencesList,
  organization,
  rowExperience,
  currentExperience,
  onStartButtonClick,
  renderLocation,
  hasSchedule,
}: StartExperienceButtonProps) => {
  const history = useGetHistory({
    body: undefined,
    pathParams: { experienceId: rowExperience.id! },
    queryParams: undefined,
  });

  const dispatch = useDispatch();

  const isStartExperimentMutating = useIsMutating({
    mutationKey: ["startExperience"],
  });
  const isEndExperimentMutating = useIsMutating({
    mutationKey: ["endExperience"],
  });
  const isPauseExperimentMutating = useIsMutating({
    mutationKey: ["pauseExperience"],
  });

  const isSubscriptionActive = useCheckSubscriptionStatus();
  const isFreeTrialActive = useCheckFreeTrialStatus();

  const isShippingAlreadyActive: ExperienceListDto[] = experiencesList
    .filter((experience) => experience.status === "started")
    .filter((experience) => experience.testTypes.hasTestShipping);
  const isPriceTestIntegrationReady = useIsPriceTestIntegrationReady();
  const scopeMap = useBooleanWebUiScopeMap();

  return (
    <ScopedStartExperienceWithDisabled
      experience={rowExperience}
      debugId={"experiment-list-button"}
      adminDisable={
        rowExperience.testTypes.hasTestShipping &&
        rowExperience.status !== "started" &&
        isShippingAlreadyActive.length > 0
      }
    >
      <ActionExperienceButton
        renderLocation={renderLocation}
        isLoading={
          history.isLoading ||
          (Boolean(
            isStartExperimentMutating ||
              isEndExperimentMutating ||
              isPauseExperimentMutating
          ) &&
            currentExperience?.id === rowExperience.id)
        }
        onClick={() => {
          onStartButtonClick(
            rowExperience,
            history?.data?.history ?? [],
            isSubscriptionActive || false
          );
        }}
        action={"start"}
        experience={rowExperience}
        hasSchedule={hasSchedule}
        onDisableClick={() => {
          if (
            rowExperience.type === "pricing" &&
            !isPriceTestIntegrationReady &&
            scopeMap["start_price_experiment"] &&
            isSubscriptionActive
          ) {
            dispatch(
              setDialog({
                dialog: "completePricingIntegration",
                open: true,
                props: rowExperience,
              })
            );
          } else if (isFreeTrialActive) {
            dispatch(
              setDialog({
                dialog: "freeTrial",
                open: true,
                props: { isPersonalization: false },
              })
            );
          } else {
            dispatch(
              setDialog({
                dialog: "billingStatus",
                open: true,
                props: rowExperience,
              })
            );
          }
        }}
      />
    </ScopedStartExperienceWithDisabled>
  );
};
